import type { WorkSheet } from '../core/WorkSheet';
import type VTableSheet from '../components/vtable-sheet';
import type { CellCoord } from '../ts-types';

interface HighlightedCell {
  row: number;
  col: number;
  color: string;
}

export class CellHighlightManager {
  private sheet: VTableSheet;
  private highlightedCells: Map<string, HighlightedCell> = new Map();
  // /** 目前还没有自定义样式的功能 这个逻辑不需要 */
  // private originalBorderStyles: Map<string, any> = new Map(); // 保存原始边框样式
  private colors = [
    '#4A90E2', // 蓝色
    '#50C878', // 绿色
    '#FF6B6B', // 红色
    '#FFD93D', // 黄色
    '#6BCB77', // 浅绿
    '#9B59B6', // 紫色
    '#FF8C42', // 橙色
    '#00BCD4' // 青色
  ];
  private colorIndex = 0;

  constructor(sheet: VTableSheet) {
    this.sheet = sheet;
  }

  /**
   * 解析公式中的单元格引用（包括范围引用）
   */
  parseCellReferences(formula: string): Array<{
    address: string;
    coords: CellCoord[];
    color: string;
    isRange: boolean;
    sheetName?: string;
  }> {
    const references: Array<{
      address: string;
      coords: CellCoord[];
      color: string;
      isRange: boolean;
      sheetName?: string;
    }> = [];

    // 更简单的跨sheet引用匹配方法
    // 匹配模式：Sheet1!A1, 'Sheet Name'!A1, A1, A1:B2, Sheet1!A1:B2
    const cellRefRegex = /(?:([']?[^!'\s]+[']?)!)?(\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?)/g;

    const uniqueRefs = new Map<string, { sheetName?: string; cellRef: string }>();
    this.colorIndex = 0;

    let match;
    while ((match = cellRefRegex.exec(formula)) !== null) {
      // 解析跨sheet引用
      const sheetNamePart = match[1];
      const cellRef = match[2];

      let sheetName: string | undefined;
      if (sheetNamePart) {
        sheetName = sheetNamePart.replace(/'/g, ''); // 移除可能的引号
      }

      const normalizedCellRef = cellRef.replace(/\$/g, '').toUpperCase();
      const uniqueKey = sheetName ? `${sheetName}!${normalizedCellRef}` : normalizedCellRef;

      if (!uniqueRefs.has(uniqueKey)) {
        uniqueRefs.set(uniqueKey, { sheetName, cellRef: normalizedCellRef });

        try {
          const isRange = normalizedCellRef.includes(':');
          let coords: CellCoord[] = [];

          // 获取目标sheet
          const targetSheet = sheetName ? this.sheet.getSheetByName(sheetName) : this.sheet.getActiveSheet();
          if (!targetSheet) {
            console.warn(`Sheet not found: ${sheetName || 'active sheet'}`);
            continue;
          }

          if (isRange) {
            // 解析范围引用，如 A1:A4
            coords = this.parseRangeReference(normalizedCellRef, targetSheet);
          } else {
            // 解析单个单元格引用，如 A1
            const coord = targetSheet.coordFromAddress(normalizedCellRef);
            coords = [coord];
          }

          if (coords.length > 0) {
            const color = this.colors[this.colorIndex % this.colors.length];
            this.colorIndex++;

            references.push({
              address: uniqueKey,
              coords: coords,
              color: color,
              isRange: isRange,
              sheetName: sheetName
            });
          }
        } catch (e) {
          // 忽略无效的单元格引用
          console.warn(`Invalid cell reference: ${uniqueKey}`, e);
        }
      }
    }

    return references;
  }

  /**
   * 解析范围引用，如 A1:A4
   */
  private parseRangeReference(rangeRef: string, activeSheet: WorkSheet): CellCoord[] {
    const parts = rangeRef.split(':');
    if (parts.length !== 2) {
      throw new Error(`Invalid range reference: ${rangeRef}`);
    }

    const startCoord = activeSheet.coordFromAddress(parts[0]);
    const endCoord = activeSheet.coordFromAddress(parts[1]);

    const coords: CellCoord[] = [];

    // 生成范围内的所有单元格坐标
    const startRow = Math.min(startCoord.row, endCoord.row);
    const endRow = Math.max(startCoord.row, endCoord.row);
    const startCol = Math.min(startCoord.col, endCoord.col);
    const endCol = Math.max(startCoord.col, endCoord.col);

    for (let row = startRow; row <= endRow; row++) {
      for (let col = startCol; col <= endCol; col++) {
        coords.push({ row, col });
      }
    }

    return coords;
  }

  /**
   * 高亮公式中引用的单元格（支持跨sheet）
   */
  highlightFormulaCells(formula: string): void {
    // 清除之前的高亮
    this.clearHighlights();

    const references = this.parseCellReferences(formula);

    if (references.length === 0) {
      return;
    }

    // 应用高亮 - 支持跨sheet
    references.forEach(ref => {
      // 获取目标sheet
      const targetSheet = ref.sheetName ? this.sheet.getSheetByName(ref.sheetName) : this.sheet.getActiveSheet();

      if (!targetSheet) {
        console.warn(`Sheet not found for highlighting: ${ref.sheetName || 'active sheet'}`);
        return;
      }

      ref.coords.forEach(coord => {
        // 使用包含sheet名称的唯一key
        const sheetPrefix = ref.sheetName ? `${ref.sheetName}!` : '';
        const key = `${sheetPrefix}${coord.row}-${coord.col}`;

        // 保存高亮信息
        this.highlightedCells.set(key, {
          row: coord.row,
          col: coord.col,
          color: ref.color
        });

        // 应用高亮样式到正确的sheet
        this.applyCellHighlight(targetSheet, coord, ref.color);
      });
    });
  }

  /**
   * 应用单元格高亮
   * 通过修改单元格的边框样式实现
   */
  private applyCellHighlight(sheet: WorkSheet, coord: CellCoord, color: string): void {
    const table = (sheet as any).tableInstance;
    if (!table || !table.scenegraph) {
      return;
    }

    try {
      const key = `${coord.row}-${coord.col}`;

      // // 保存原始样式
      // if (!this.originalBorderStyles.has(key)) {
      //   const originalStyle = table.getCellStyle(coord.col, coord.row);
      //   this.originalBorderStyles.set(key, originalStyle);
      // }

      // 创建高亮样式
      const highlightStyle = {
        // ...table._getCellStyle(coord.col, coord.row),
        borderColor: [color, color, color, color],
        borderLineWidth: [4, 4, 4, 4],
        borderLineDash: [
          [4, 4],
          [4, 4],
          [4, 4],
          [4, 4]
        ],
        padding: [8, 8, 8, 8]
      };

      // 注册自定义样式
      const styleId = `highlight-${key}`;
      table.registerCustomCellStyle(styleId, highlightStyle);

      // 应用样式到单元格
      table.arrangeCustomCellStyle({ col: coord.col, row: coord.row }, styleId, false);
    } catch (e) {
      console.error('applyCellHighlight: 错误', e);
    }
  }

  /**
   * 清除所有高亮
   */
  clearHighlights(): void {
    const activeSheet = this.sheet.getActiveSheet();
    if (!activeSheet) {
      return;
    }

    const table = (activeSheet as any).tableInstance;
    if (!table || !table.scenegraph) {
      return;
    }

    this.highlightedCells.forEach(info => {
      try {
        // 移除高亮样式
        table.arrangeCustomCellStyle({ col: info.col, row: info.row }, null, false);

        // // 恢复原始样式
        // const originalStyle = this.originalBorderStyles.get(key);
        // if (originalStyle) {
        //   table.registerCustomCellStyle(styleId, originalStyle);
        //   table.arrangeCustomCellStyle({ col: info.col, row: info.row }, styleId, true);
        // }
      } catch (e) {
        console.warn(`Failed to clear highlight at ${info.row},${info.col}:`, e);
      }
    });

    this.highlightedCells.clear();
    // this.originalBorderStyles.clear();
    this.colorIndex = 0;
  }

  /**
   * 获取单元格的高亮颜色
   */
  getCellHighlightColor(coord: CellCoord): string | null {
    const key = `${coord.row}-${coord.col}`;
    const info = this.highlightedCells.get(key);
    return info ? info.color : null;
  }

  /**
   * 获取高亮的单元格列表
   */
  getHighlightedCells(): Array<{ coord: CellCoord; color: string }> {
    return Array.from(this.highlightedCells.values()).map(info => ({
      coord: { row: info.row, col: info.col },
      color: info.color
    }));
  }

  /**
   * 获取彩色公式文本
   */
  getColorizedFormula(formula: string): Array<{ text: string; color?: string }> {
    const references = this.parseCellReferences(formula);
    const parts: Array<{ text: string; color?: string }> = [];

    if (references.length === 0) {
      return [{ text: formula }];
    }

    let lastIndex = 0;
    const colorMap = new Map<string, string>();
    references.forEach(ref => {
      colorMap.set(ref.address.toUpperCase(), ref.color);
    });

    const regex = /(\$?[A-Z]+\$?\d+)/gi;
    let match;

    while ((match = regex.exec(formula)) !== null) {
      if (match.index > lastIndex) {
        parts.push({ text: formula.substring(lastIndex, match.index) });
      }

      const normalizedRef = match[0].replace(/\$/g, '').toUpperCase();
      const color = colorMap.get(normalizedRef);
      parts.push({
        text: match[0],
        color: color
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < formula.length) {
      parts.push({ text: formula.substring(lastIndex) });
    }

    return parts;
  }

  /**
   * 销毁管理器
   */
  release(): void {
    this.clearHighlights();
    this.highlightedCells.clear();
    // this.originalBorderStyles.clear();
  }
}
